import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import sortBy from '@Ravnur-Inc/nanoutils/sortBy';
import { renderSlot } from 'vue';
import { Vue, Options, prop } from 'vue-class-component';
import Draggable from 'vuedraggable';

import { OrderableEntity as OE } from '@/types/Entity';
import { uniqueId } from 'lodash';

import './reordering-list.scss';

const CN = 'reordering-list';
const sortByOrder = sortBy(<E extends OE>(e: E) => e.order);

class Props<E extends OE> {
  list = prop<E[]>({ default: [] });
  localId = prop<boolean>({ default: false });
}

type Emits<E extends OE> = {
  'onUpdate:list': (items: E[]) => void;
};

@Options({
  name: 'reordering-list',
  emits: ['update:list'],
})
export default class ReorderingList<E extends OE> extends Vue.with(Props) {
  declare $props: $Props<Props<E>, Emits<E>, 'list'>;
  declare list: E[];

  get entities(): E[] {
    return sortByOrder(this.list).map((i) => {
      if (this.localId) {
        return { ...i, id: uniqueId() };
      } else {
        return i;
      }
    });
  }

  set entities(entities: E[]) {
    const list = entities.map((entity, order) => ({ ...entity, order }));
    this.$emit('update:list', list);
  }

  render() {
    return (
      <scrollable class={CN}>
        <Draggable
          class={`${CN}__list`}
          ghost-class={`${CN}__ghost`}
          itemKey="id"
          scroll-sensitivity={1}
          tag="ul"
          v-model={this.entities}
          v-slots={{ item: this.renderItem }}
        />
      </scrollable>
    );
  }

  private renderItem({ element: entity }: { element: E }) {
    return (
      <li key={entity.id} class={`${CN}__item`}>
        <icon class={`${CN}__icon`} type="drag" />
        <div class={`${CN}__item-snippet`}>{renderSlot(this.$slots, 'item', { entity })}</div>
      </li>
    );
  }
}
